import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "idle",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    assignEmployeeStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null,
};

// Fetch employees list
export const fetchEmployees = createAsyncThunk('employee/fetchEmployees',
    async ({scopeId, roleSign, page, limit}) => {
        try {
            const response = await api.get(`/${roleSign}/member?id=${scopeId}&page=${page}&limit=${limit}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create a new employee
export const createEmployee = createAsyncThunk('employee/createEmployee',
    async ({data, roleSign}) => {
        try {
            const response = await api.post(`/${roleSign}/member`, data, {
                useMultipart: true,
            });
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch employee by ID
export const fetchEmployeeById = createAsyncThunk('employee/fetchEmployeeById',
    async (id) => {
        try {
            const response = await api.get(`/adg/member/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update employee
export const updateEmployee = createAsyncThunk('employee/updateEmployee',
    async ({id, data}) => {
        try {
            const response = await api.patch(`/adg/member/${id}`, data, {
                useMultipart: true,
            });
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Assign employee
export const assignEmployee = createAsyncThunk('employee/assignEmployee',
    async ({data, roleSign}) => {
        try {
            const response = await api.post(`/${roleSign}/member/roleAssign`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete employee
export const deleteEmployee = createAsyncThunk('employee/deleteEmployee',
    async (id) => {
        try {
            const response = await api.delete(`/adg/member/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const employeeSlice = createSlice({
    name: "employee",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetEmployeeAssignStatus(state) {
            state.assignEmployeeStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch employees
            .addCase(fetchEmployees.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchEmployees.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchEmployees.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create employee
            .addCase(createEmployee.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createEmployee.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createEmployee.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch employee by ID
            .addCase(fetchEmployeeById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchEmployeeById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchEmployeeById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update employee
            .addCase(updateEmployee.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateEmployee.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateEmployee.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Assign employee
            .addCase(assignEmployee.pending, (state) => {
                state.assignEmployeeStatus = 'loading';
            })
            .addCase(assignEmployee.fulfilled, (state, action) => {
                const {assignEmployeeStatus, error} = handleResponseStatus(action.payload, 'assignEmployeeStatus');
                state.assignEmployeeStatus = assignEmployeeStatus;
                state.error = error;
            })
            .addCase(assignEmployee.rejected, (state, action) => {
                state.assignEmployeeStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete employee
            .addCase(deleteEmployee.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteEmployee.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteEmployee.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetCreateStatus,
    resetEmployeeAssignStatus
} = employeeSlice.actions;

export default employeeSlice.reducer;
